import { FetchResult, NotificationsResponse } from 'src/types/TApiQueries';
import { TTaskAction } from 'src/types/TNotification';
import { config } from 'src/utils/config';
import { performFetchWithLogging, getStandardHeaders } from '../utils/helpers';

export async function getAllNotifications(accessToken: string): Promise<FetchResult<NotificationsResponse>> {
  const url = `${config.hostUrl}/api/v1/user/notifications`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

// Reviews the caller ASSIGNED to other people — the other half of the loop
// from `getAllNotifications`, which only ever answers "what is waiting on me".
// Rows carry `assigned_to`/`assigned_to_email` (the reviewer) and INCLUDE
// resolved tasks, because "she declined it" is the most useful thing here.
export async function getAssignedReviews(
  accessToken: string,
  openOnly = false,
): Promise<FetchResult<NotificationsResponse>> {
  const qs = openOnly ? '?assigned_by=me&open=1' : '?assigned_by=me';
  const url = `${config.hostUrl}/api/v1/user/notifications${qs}`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function deleteNotifications(notifications: number[], accessToken: string): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/user/notifications`;

  const payload = JSON.stringify(notifications);

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: payload,
  });
}

// Resolves a task notification (Complete / Decline / Dismiss). Backed by
// `PATCH /api/v1/user/notifications/{id}`; the backend updates the task status
// and notifies the assigner of the outcome.
export async function resolveTask(
  notificationId: number,
  action: TTaskAction,
  accessToken: string,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/user/notifications/${notificationId}`;

  return performFetchWithLogging(url, {
    method: 'PATCH',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ action }),
  });
}

// Sends an outbound notification (e.g. email a collection deep link to a
// teammate). Backed by `POST /api/v1/notifications/send`; the backend
// enforces the group's recipient allow-list and returns the accepted /
// rejected recipients.
export type SendNotificationPayload = {
  channels?: string[];
  to: string[];
  subject: string;
  body: string;
  body_html?: string;
  // Optional deep link stored on the recipient's in-app notification (`url`
  // column) so they can click straight through to the shared resource.
  url?: string;
  // Review shape. Applied to the in-app task raised for in-account
  // recipients; an external recipient gets the email either way, and there is
  // nowhere to hang a deadline on a plain mail.
  intent?: 'approve' | 'comment' | 'fix';
  // ISO date (YYYY-MM-DD, read as end of that day UTC) or RFC 3339 timestamp.
  due_on?: string;
};

export async function sendNotification(
  payload: SendNotificationPayload,
  accessToken: string,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/notifications/send`;

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ channels: ['email'], ...payload }),
  });
}
